import { DateTime } from 'luxon';
import { ZONA_OPERATIVA } from '../tipos/comunes.js';
import { asignarRecursos } from './asignacion.js';
import { exigirMultiplosDeSlot } from './granularidad.js';
import { resolverServicio } from './cascada.js';
/**
 * `calcularSlots` — la unica implementacion de "que horarios puedo ofrecer".
 *
 * La usan la PWA, la API y el sitio publico. Dos implementaciones divergen y
 * producen bugs de agenda irreproducibles.
 *
 * La enumeracion es exhaustiva sobre la grilla de `slotMin`: si devuelve cero
 * slots es porque no existe ninguna asignacion valida en la ventana, no porque
 * la busqueda se haya rendido antes.
 */
/** Primer instante de la grilla de `slotMin` que no es anterior a `fecha`. */
export function alinearASlot(fecha, slotMin, zona = ZONA_OPERATIVA) {
    const local = DateTime.fromJSDate(fecha, { zone: zona });
    const medianoche = local.startOf('day');
    const minutos = local.diff(medianoche, 'minutes').minutes;
    const pasos = Math.ceil(minutos / slotMin);
    return medianoche.plus({ minutes: pasos * slotMin }).toJSDate();
}
export function duracionTotalMin(entrada) {
    return entrada.servicio.tramos.reduce((total, tramo) => total + tramo.duracionMin, 0);
}
export function calcularSlots(entrada) {
    if (!entrada.duracionesPorPersona || entrada.duracionesPorPersona.length === 0) {
        return calcularSlotsDeGrupo(entrada);
    }
    const { servicio, recursos, tipos, recursoPreferido, duracionesPorPersona } = entrada;
    const tiposRequeridos = new Set(servicio.tramos.flatMap((t) => t.requisitos.map((r) => r.recursoTipoId)));
    const requierePrincipal = Array.from(tiposRequeridos).some((tipoId) => tipos.find((t) => t.id === tipoId)?.seleccionablePorCliente);
    if (!requierePrincipal) {
        return calcularSlotsDeGrupo(entrada);
    }
    const reqEspecialidad = servicio.especialidadRequeridaId;
    let candidatos = recursos.filter((r) => {
        const tipo = tipos.find((t) => t.id === r.tipoId);
        return r.activo &&
            tiposRequeridos.has(r.tipoId) &&
            tipo?.seleccionablePorCliente &&
            (!reqEspecialidad || !tipo?.esPersona || (r.especialidades ?? []).includes(reqEspecialidad));
    });
    if (recursoPreferido) {
        candidatos = candidatos.filter((r) => r.id === recursoPreferido);
    }
    const grupos = [];
    const idsCatalogo = new Set();
    for (const c of candidatos.sort((a, b) => a.id.localeCompare(b.id))) {
        const resuelto = resolverServicio(servicio, c.id, duracionesPorPersona);
        // Comparar el buffer efectivo: un override explícito igual al del tipo
        // pertenece al catálogo tanto como uno nulo o ausente.
        const esCatalogo = resuelto.tramos.every((tramo, i) => {
            const catalogo = servicio.tramos[i];
            return tramo.duracionMin === catalogo.duracionMin && tramo.requisitos.every((req) => {
                const tipo = tipos.find((t) => t.id === req.recursoTipoId);
                return !tipo || tipo.esPersona ||
                    (tramo.bufferMin ?? tipo.bufferMin) === (catalogo.bufferMin ?? tipo.bufferMin);
            });
        });
        if (esCatalogo) {
            idsCatalogo.add(c.id);
        }
        else {
            // Un perfil distinto del catálogo conserva la misma persona en todos
            // los tramos, aunque otra persona tenga exactamente esos overrides.
            grupos.push({ servicio: resuelto, ids: new Set([c.id]) });
        }
    }
    if (idsCatalogo.size > 0) {
        grupos.unshift({ servicio, ids: idsCatalogo });
    }
    const slotsUnidos = new Map();
    for (const grupo of grupos) {
        const entradaGrupo = { ...entrada, servicio: grupo.servicio };
        const slots = calcularSlotsDeGrupo(entradaGrupo, grupo.ids);
        for (const slot of slots) {
            const tiempo = slot.inicio.getTime();
            if (!slotsUnidos.has(tiempo)) {
                slotsUnidos.set(tiempo, slot);
            }
        }
    }
    return Array.from(slotsUnidos.values()).sort((a, b) => a.inicio.getTime() - b.inicio.getTime());
}
function calcularSlotsDeGrupo(entrada, principalesPermitidos) {
    const { servicio, slotMin } = entrada;
    const zona = entrada.zona ?? ZONA_OPERATIVA;
    // Una duracion irregular falla fuerte. Devolver cero slots en silencio haria
    // que el error se descubra recien cuando un cliente no encuentra turnos.
    exigirMultiplosDeSlot(servicio.tramos.map((t) => t.duracionMin), slotMin);
    const total = duracionTotalMin(entrada);
    if (total <= 0)
        return [];
    const { desde, hasta } = limitesPorAnticipacion(entrada);
    if (desde.getTime() >= hasta.getTime())
        return [];
    const slots = [];
    const paso = slotMin * 60_000;
    const ultimoInicio = hasta.getTime() - total * 60_000;
    for (let inicio = alinearASlot(desde, slotMin, zona); inicio.getTime() <= ultimoInicio; inicio = new Date(inicio.getTime() + paso)) {
        const tramos = asignarRecursos(entrada, inicio, principalesPermitidos);
        if (!tramos)
            continue;
        slots.push({
            inicio,
            fin: new Date(inicio.getTime() + total * 60_000),
            tramos,
        });
    }
    return slots;
}
/** §7.3 — la reserva publica respeta `anticipacion_min_h` y `anticipacion_max_d`. */
function limitesPorAnticipacion(entrada) {
    const { ventana, servicio } = entrada;
    let desde = ventana.inicio;
    let hasta = ventana.fin;
    if (entrada.canalPublico) {
        const ahora = entrada.ahora ?? new Date();
        if (servicio.anticipacionMinH !== undefined) {
            const minimo = new Date(ahora.getTime() + servicio.anticipacionMinH * 3_600_000);
            if (minimo.getTime() > desde.getTime())
                desde = minimo;
        }
        if (servicio.anticipacionMaxD !== undefined) {
            const maximo = new Date(ahora.getTime() + servicio.anticipacionMaxD * 86_400_000);
            if (maximo.getTime() < hasta.getTime())
                hasta = maximo;
        }
    }
    return { desde, hasta };
}
//# sourceMappingURL=disponibilidad.js.map